import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { I18nProvider, useTranslation } from '@mukil/i18n';
import { Toaster } from '@jumpstone/ui-react';
import { App } from './App.js';
import { WageProStoreProvider } from './data/wagepro_store.js';
import { resolveOrganisationActions } from './data/use_settings.js';
import { usePayRunPostActions } from './data/use_pay_run_post_actions.js';
import { useSessionFacts } from './data/use_session_facts.js';
import {
  GrantErrorScreen,
  NoPayrollAccessScreen,
  SessionErrorScreen,
  SignedOutScreen,
} from './components/auth/session_boot_states.js';
import {
  WAGEPRO_TOASTER_MOBILE_OFFSET,
  WAGEPRO_TOASTER_OFFSET,
} from './components/layout/toaster_offset.js';
import { ConnectLandingGate } from './components/auth/connect_landing_gate.js';
import {
  BootPlaceholder,
  BootPlaceholderFrame,
} from './components/auth/wagepro_splash.js';
import {
  AuthProvider,
  useAuth,
  type AuthStatus,
} from './lib/auth/auth_context.js';
import { useOAuthEntityContext } from './lib/auth/oauth_entity_context.js';
import {
  resolveAuthGateRedirect,
  resolveAuthGateScreen,
} from './lib/auth/auth_gate_screen.js';
import { SIGNUP_PATH } from './lib/auth/wagepro_signup_logic.js';
import {
  LOGIN_PATH,
  buildLedgerSsoLoginPath,
  buildLoginPath,
} from './lib/auth/wagepro_login_logic.js';
import {
  INVITE_ACCEPT_PATH,
  INVITE_CODE_PATH,
} from './lib/auth/invite_accept_logic.js';
import { WageProSignupScreen } from './screens/auth/wagepro_signup_screen.js';
import { WageProLoginScreen } from './screens/auth/wagepro_login_screen.js';
import { InviteAcceptScreen } from './screens/auth/invite_accept_screen.js';
import { InviteCodeScreen } from './screens/auth/invite_code_screen.js';
import {
  completeSignIn,
  getOAuthCallbackError,
  isOAuthCallbackUrl,
} from './lib/auth/oauth_pkce.js';
import { handleOAuthCallback } from './lib/auth/handle_oauth_callback.js';
import {
  currentRuntimeTarget,
  resetTrpcClient,
  resetBearerTrpcClient,
  resolveApiUrl,
} from './lib/trpc.js';
import { resolveOAuthResource } from './lib/api_target.js';
import { initI18n, switchLocale } from './lib/i18n.js';
import { create_latest_wins } from './lib/latest_wins.js';
import { register_native_file_open_on_notification_tap } from './lib/native_file_open.js';
import type { I18nState, WageProLocale } from './lib/i18n.js';
import './index.css';

const container = document.getElementById('root');
if (!container) {
  throw new Error('Root container #root not found');
}

const root = createRoot(container);

// The same placeholder renders during i18n boot AND the "Sign in
// with Ledger" auth boot below — one loading frame, not two different ones.
// The "Loading your WagePro account…" copy (issue #23952) is only correct AFTER an
// authenticated session has resolved and we are threading through the
// post-consent boot; for a cold `loading`/`unauthenticated`/`error`/
// `signed_out` paint, the copy would misdescribe what is actually
// happening (we are NOT loading the account yet — we are waiting on
// credentials, waiting on the local session, or in the middle of a sign-
// out) and was the LOW-severity second adversarial-review finding on PR
// #23958. The neutral placeholder covers those cases; only the
// authenticated-in-flight branch swaps in the localised copy.
const BOOT_PLACEHOLDER = <BootPlaceholderFrame />;

function LocalisedBootPlaceholder({
  status,
  forceConnectingCopy,
}: {
  /**
   * Current {@link AuthStatus} at the call site. When present, the
   * "Loading your WagePro account…" copy only renders for `'authenticated'` —
   * the only state in which there is an account to load (post-consent boot,
   * or a returning session fetching its facts). Every other state (`loading` — waiting on
   * the local session, `unauthenticated` — no session at all, `error` — IdP
   * rejected sign-in, `signed_out` — deliberate sign-out in flight,
   * `needs_ledger_sso` — SSO redirect in flight) renders the neutral
   * placeholder instead.
   */
  status?: AuthStatus;
  /**
   * Set to `true` at call sites that KNOW the user is actively mid-Ledger-
   * callback and no `AuthStatus` is available yet (see
   * {@link OAuthCallbackBoot}, which mounts BEFORE `<AuthProvider>`).
   * Overrides the {@link status} gate.
   */
  forceConnectingCopy?: boolean;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  const showConnectingCopy =
    forceConnectingCopy === true || status === 'authenticated';
  // Same states and the same copy rule as always. The PRESENTATION differs
  // by runtime: the native shell gets the branded splash, the web app keeps
  // the neutral placeholder it has always had. See `BootPlaceholder`.
  return (
    <BootPlaceholder
      caption={showConnectingCopy ? t('boot.loading_account') : undefined}
    />
  );
}

// Native shell only: tapping a "Payslip downloaded" notification opens the
// saved PDF. Registered before the first render so a tap that cold-started
// the app is still delivered (see lib/native_file_open.ts).
register_native_file_open_on_notification_tap();

// Paint a neutral placeholder until the translation bundle resolves, so the
// first frame is never a flash of untranslated English.
root.render(<React.StrictMode>{BOOT_PLACEHOLDER}</React.StrictMode>);

/**
 * Gates the real app behind a resolved "Sign in with Ledger" session.
 * `loading` and `unauthenticated` render the same neutral placeholder. Only
 * `unauthenticated` kicks off a full-page redirect to the authorize
 * endpoint.
 *
 * `AuthGate` has exactly one gate now — the real `useSessionFacts()`/
 * `useOAuthEntityContext()` pair below. The former dev-bypass boot-time data
 * fan-out (`useDevBackendData` -> `resolveDevBackendData` ->
 * `resolveDevEntityContext`) is DELETED (C12, #22334) -- C1-C11 replaced
 * every fan-out target with a real, self-fetching, OAuth-bearer hook that
 * runs in every environment, leaving a resolver whose result was already
 * discarded (`.then(() => {...})` took no parameter). `dev_auth_bypass.ts`
 * survives: it establishes a real localhost session cookie so
 * `GET /oauth/authorize` lands on a logged-in request; it resolves no data.
 *
 * TWO of `AuthStatus`'s values reach a REAL, visible screen — checked BEFORE
 * the neutral-placeholder fallthrough below, not after (fixed 2026-07, PR
 * #22341 adversarial review findings #1/#2 — see `AuthStatus`'s docblock in
 * `lib/auth/auth_context.tsx` for the full history of why this ordering
 * matters):
 *
 * - `error` means the IdP just rejected the sign-in attempt (bad scope,
 *   denied consent, …). Auto-redirecting there would recreate the same
 *   error and loop forever, so it deliberately stays put on the neutral
 *   placeholder instead of retrying — there's genuinely nothing a retry can
 *   do here.
 * - `grant_error` means sign-in itself succeeded but `resolveAuthBoot`
 *   couldn't resolve the token's scoped grant even after its own retry.
 *   Unlike `error`, this IS worth a user-triggered retry, so it renders
 *   `<GrantErrorScreen retry={retryBoot} />` — a real, reachable screen with
 *   a visible message and a working retry action (this used to reuse
 *   `error` and consequently was NEVER reachable, since the guard below
 *   caught it first and rendered a permanently blank page).
 *
 * Once `status === 'authenticated'`, a SECOND gate (C1, #22323) sits in
 * front of the real app: `useSessionFacts()` — real `payroll_role`/
 * `is_adviser`/`company_name`, resolved via the shared
 * `useOAuthEntityContext()` helper from the token's `auth.oauthGrant`.
 * `no_entity` (the grant has no bound entity — an identity-only consent, or
 * a token minted before any entity existed) renders
 * `<NoPayrollAccessScreen>`, never a session built from `MOCK_SESSION` — see
 * `use_session_facts.ts`'s module docblock for why there is no mock
 * fallback here. `error` (the real fetch itself failed) renders
 * `<SessionErrorScreen>` with a retry action. Only `ready` proceeds to mount
 * `<WageProStoreProvider>`/`<App>`.
 *
 * `sessionFactsState.status === 'ready'` also means the Organisation-settings
 * read succeeded (C11, #22333 -- see `resolveSessionFactsState`'s doc
 * comment on why `organisation` resolves in lock-step with `facts`), so
 * `organisation_record`/`organisation_actions` are threaded straight from
 * `sessionFactsState`/`resolveOrganisationActions` below rather than from any
 * dev-bypass-only fan-out.
 */
function AuthGate(): React.JSX.Element {
  const { status, identity, grant, sessionKind, employee, retryBoot, signOut } =
    useAuth();
  const { state: sessionFactsState, retry: retrySessionFacts } =
    useSessionFacts();
  // Real production Organisation-settings entity context (C11, #22333) --
  // the SAME shared helper `useSessionFacts()` already uses internally, not
  // a second independent resolution. See `use_settings.ts`'s
  // `resolveOrganisationActions` doc comment for the `hasRealOrganisation`
  // guard this feeds.
  const entityContext = useOAuthEntityContext();
  // Real, OAuth-backed post-run actions controller — payslip/disbursement
  // /bank-picker (C8, #22330) AND statutory payments/filing calendar (C9,
  // #22331), reachable in every environment, never gated by dev bypass. This
  // hook already memoizes its own return value (keyed on the entity context
  // + grant) so it keeps a STABLE object identity across unrelated
  // `AuthGate` re-renders — `post_run_sections.tsx`'s dialogs list
  // `post_actions` as a `useEffect` dependency (the disbursement/statutory
  // account pickers, the statutory-payments list, the filing-obligations
  // list), so a fresh identity every render would re-fire every one of
  // those fetches on every unrelated re-render of this component
  // (adversarial-review finding, C8, #22330 — the reasoning still applies).
  const pay_run_post_actions = usePayRunPostActions();

  // Memoized (adversarial-review finding, C11 #22333): `resolveOrganisationActions`
  // returns a brand-new object (a fresh `save` closure) on every call. Calling
  // it inline in the JSX below made `organisation_actions` a NEW reference on
  // EVERY render of this component -- and since it's a dependency of
  // `wagepro_store.tsx`'s own top-level `useMemo`, that invalidated the whole
  // store's memoized value (and every `useWageProStore()` consumer's render)
  // on every `AuthGate` render, not just when the underlying auth/session
  // state actually changed. `entityContext` is already stable across renders
  // (`useOAuthEntityContext`'s own `useMemo`, keyed on `grant`); pairing it
  // with the primitive `sessionFactsStatus` local (destructured below, never
  // the whole non-memoized `sessionFactsState` object) keeps this memoized
  // too. `retrySessionFacts` is also stable (`useCallback`, `use_session_
  // facts.ts`), so adding it as a dependency doesn't reintroduce the same
  // render-churn bug this memo exists to prevent.
  //
  // `sessionFactsStatus` is destructured into its own const, and
  // `resolveOrganisationActions` itself now takes that primitive (not the
  // whole `SessionFactsResult` object) -- so a future change to this
  // callback's body that starts reading another field off `sessionFactsState`
  // is forced to widen the function signature (and this dependency array) to
  // say so, rather than silently closing over a field neither declares
  // (second-round adversarial-review finding, C11 #22333: this eslint config
  // does not enable `react-hooks/exhaustive-deps`, so nothing else would
  // catch that regression).
  const { status: sessionFactsStatus } = sessionFactsState;
  const organisationActions = useMemo(
    () =>
      resolveOrganisationActions(
        entityContext,
        sessionFactsStatus,
        retrySessionFacts,
      ),
    [entityContext, sessionFactsStatus, retrySessionFacts],
  );

  // Both `'unauthenticated'` (cold boot) and `'signed_out'` (the user just
  // pressed Sign out) auto-redirect — but with DIFFERENT intents, which is
  // the entire reason this goes through `resolveAutoSignInPrompt` rather
  // than an inline status check.
  //
  // Before the WagePro-branded `/login` screen existed, this redirected
  // straight to `GET /oauth/authorize`, which (with no session cookie) sent
  // the user through Ledger's own generic, un-WagePro-branded sign-in page
  // before they ever saw anything WagePro-branded. It now navigates to OUR
  // `/login` screen instead — the actual "Sign in with Ledger" PKCE handoff
  // still happens (`beginSignIn()`, unchanged), it just now runs FROM that
  // screen, AFTER the user has entered valid credentials there, rather than
  // before landing on any page at all.
  //
  // A deliberate sign-out still needs the equivalent of the old
  // `prompt=login` guarantee: without it, a still-live Ledger session cookie
  // (which `signOut()` cannot end — it is host-scoped and shared with the
  // dashboard) could make "Sign out" look like a no-op. That guarantee is
  // now provided STRUCTURALLY instead of via an OIDC prompt param: `/login`
  // always requires a fresh email/password submission — there is no path
  // from it that silently reuses an existing cookie — so `resolveAutoSignInPrompt`
  // returning a non-empty prompt (the `signed_out` case) is threaded through
  // to `buildLoginPath` only as `FORCE_REAUTH_PARAM`, so the login screen can
  // show a "you were signed out" message. See `wagepro_login_logic.ts`'s
  // docblock on that constant for why it is deliberately NOT propagated
  // further into that screen's own later `beginSignIn()` call — doing so
  // would force a REDUNDANT second password prompt at Ledger's own sign-in
  // page, defeating the "carries straight through" property this app's
  // signup screen already relies on.
  //
  // If the redirect cannot even be constructed, `sign_out_redirect_failed`
  // falls back to `SignedOutScreen`, whose button does the same navigation
  // manually — a dead end is the one case worth showing a card for.
  const [sign_out_redirect_failed, set_sign_out_redirect_failed] =
    useState(false);
  useEffect(() => {
    // `resolveAuthGateRedirect` discriminates FOUR destinations
    // (`auth_gate_screen.ts`): (1) do nothing, (2) plain `/login` no-prompt
    // (cold boot), (3) plain `/login` with the "you were signed out" hint
    // (deliberate sign-out), (4) the Ledger-SSO handoff (a cookie identity
    // that would 403 at `wageproAuth.login` — see the `needs_ledger_sso`
    // branch of `AuthStatus` in `auth_context.tsx`).
    const target = resolveAuthGateRedirect(status);
    if (target === null) return;
    try {
      window.location.href =
        target === 'ledger_sso'
          ? buildLedgerSsoLoginPath()
          : buildLoginPath({ forceReauth: target.forceReauth });
    } catch {
      // Only the sign-out path has a fallback screen to fall back TO; a cold
      // boot that cannot even build a login URL is already covered by the
      // `error`/placeholder handling above. Mirrors the pre-existing
      // beginSignIn()-failure handling this replaced.
      if (status === 'signed_out') set_sign_out_redirect_failed(true);
    }
  }, [status]);

  // A fresh sign-out after a previously-failed one must get a real attempt,
  // not the stale failure card.
  useEffect(() => {
    if (status === 'authenticated') set_sign_out_redirect_failed(false);
  }, [status]);

  // `resolveAuthGateScreen` (`lib/auth/auth_gate_screen.ts`) is the pure,
  // unit-tested source of truth for this ordering — see its module docblock
  // for why `grant_error` MUST be checked before the blanket
  // `status !== 'authenticated'` fallthrough (PR #22341 findings #1/#2).
  const screen = resolveAuthGateScreen({
    status,
    sessionFactsStatus: sessionFactsState.status,
    signOutRedirectFailed: sign_out_redirect_failed,
    sessionKind,
  });

  if (screen === 'grant_error') {
    return <GrantErrorScreen retry={retryBoot} />;
  }

  // Only reachable when the automatic post-sign-out redirect could not be
  // started (see the effect above) — the happy path never renders this.
  // `SignedOutScreen`'s own primary action now navigates to the
  // WagePro-branded `/login` screen rather than re-invoking `beginSignIn()`
  // here directly — see its docblock in `session_boot_states.tsx`.
  if (screen === 'signed_out') {
    return <SignedOutScreen />;
  }

  if (screen === 'boot_placeholder') {
    return <LocalisedBootPlaceholder status={status} />;
  }

  if (screen === 'no_payroll_access') {
    return <NoPayrollAccessScreen signOut={signOut} />;
  }

  if (screen === 'session_error') {
    return <SessionErrorScreen retry={retrySessionFacts} />;
  }

  // Employee (self-service) session: its facts come from the BOOT's own
  // `EmployeeSelfContext`, not from `useSessionFacts` — see
  // `resolveAuthGateScreen`'s `sessionKind === 'employee'` branch for why that
  // hook can never resolve here.
  //
  // `payroll_role: 'employee'` is passed explicitly rather than left to
  // `deriveRole`'s fallback: that fallback keys off `is_adviser`/`manage_users`,
  // and `deriveSessionFromFacts` returns `base` UNCHANGED when facts are
  // absent — where `base` is `MOCK_SESSION`, whose role is `'admin'`. Booting
  // an employee with absent facts would therefore drop them onto the ADMIN
  // surface (`App.tsx` branches on `is_admin`), which is the exact class of bug
  // PR #22341 finding #3 fixed once already.
  //
  // No `organisation_record` is passed. It carries `financial_year_end` and
  // `currency_code`, which only `entity.clientGet` (employer-scoped) can supply;
  // synthesizing them would be fabricated financial config, and the prop is
  // optional precisely so a session without a real one goes without. The
  // Settings surface it feeds is admin-only and unreachable from `!is_admin`.
  if (sessionKind === 'employee' && employee !== null) {
    return (
      <WageProStoreProvider
        identity={identity ?? undefined}
        grant={grant ?? undefined}
        session_facts={{
          payroll_role: 'employee',
          is_adviser: false,
          manage_users: false,
          entity_role: 'readonly',
          firm_role: 'member',
          company_name: employee.entityName,
        }}
        is_employee_session
      >
        <App />
      </WageProStoreProvider>
    );
  }

  // Unreachable in practice: `resolveAuthGateScreen` only returns `'app'`
  // when `sessionFactsState.status === 'ready'` (see its implementation).
  // This check exists purely so TypeScript can narrow `sessionFactsState` to
  // the `ready` variant below -- `screen` and `sessionFactsState.status` are
  // two separate variables from TS's point of view, so the narrowing the
  // pure function performs internally doesn't carry across the call
  // boundary.
  if (sessionFactsState.status !== 'ready') {
    return <LocalisedBootPlaceholder status={status} />;
  }

  // Post-consent landing decision (issue #23952): wrap the store/App in
  // `ConnectLandingGate`, which reads `wageproAuth.organisationStatus` and
  // routes an admin whose WagePro company row / payroll entitlement isn't
  // provisioned yet onto the "Set up payroll for <Org>" first-run screen —
  // and a payroll-role-less firm member onto a "no payroll role" fallback
  // (replacing the old bare dead-end). `resolveConnectLanding`
  // (`lib/auth/connect_landing_logic.ts`) is the pure decision function.
  //
  // The gate always mounts the store first (never below `sessionFactsState
  // === ready`), so even the setup screen has the same session context;
  // its own inputs (entityContext + facts) are threaded explicitly rather
  // than pulled off `useAuth()` inside the gate so the decision inputs are
  // spelled out at the mount site.
  return (
    <WageProStoreProvider
      identity={identity ?? undefined}
      grant={grant ?? undefined}
      session_facts={sessionFactsState.facts}
      pay_run_post_actions={pay_run_post_actions}
      organisation_record={sessionFactsState.organisation}
      organisation_actions={organisationActions}
    >
      <ConnectLandingGateBridge facts={sessionFactsState.facts}>
        <App />
      </ConnectLandingGateBridge>
    </WageProStoreProvider>
  );
}

/**
 * Reads the entity context the same way every real hook in this app does
 * (`useOAuthEntityContext()`) and hands it, plus the already-resolved
 * employer facts, to {@link ConnectLandingGate}. Kept as a separate
 * component so `useOAuthEntityContext()` is called inside the store's tree
 * (mirroring the store's other consumers) rather than at the `AuthGate`
 * level where the memo above tried to keep its identity stable.
 */
function ConnectLandingGateBridge({
  facts,
  children,
}: {
  facts: import('./data/wagepro_store_session_facts.js').SessionFacts;
  children: React.ReactNode;
}): React.JSX.Element {
  const entityContext = useOAuthEntityContext();
  if (entityContext.status !== 'ready') {
    // `resolveAuthGateScreen`'s `no_entity` branch means we should never
    // reach this component with an unready context — this is a
    // TS-narrowing guard, not a runtime path.
    return <>{children}</>;
  }
  return (
    <ConnectLandingGate entityContext={entityContext} facts={facts}>
      {children}
    </ConnectLandingGate>
  );
}

/**
 * OAuth callback gate — runs BEFORE `<AuthProvider>` mounts.
 *
 * When the browser returns to `/auth/callback?code=…&state=…` after Ledger's
 * consent screen, WagePro must exchange the code AND persist the token pair
 * BEFORE any auth machinery boots — otherwise `resolveAuthBoot` runs against
 * a session that does not yet hold the WagePro-audience OAuth token minted
 * by the consent, so its Ledger-first branch (`auth.oauthGrant`) has nothing
 * to observe and the user falls through to `unauthenticated`. That is the
 * root cause of the "first-run screen unreachable" BLOCKER from the
 * adversarial review of f2a602384.
 *
 * Same shape as `apps/costpro-web/src/App.tsx`'s `useOAuthCallback` hook —
 * ported verbatim, only the imports and the "home" target differ. StrictMode
 * double-invoke is dedup'd two ways: {@link completeSignIn} itself dedupes
 * on a module-level in-flight promise, and `startedRef` here guards the
 * post-exchange side effects (bearer-client reset + navigation) so they
 * cannot run twice on an already-resolved landing.
 *
 * On an IdP `error` redirect (`?error=access_denied` — the user declined
 * consent) we render the app's own {@link GrantErrorScreen} and NEVER
 * auto-navigate back into `/oauth/authorize`. Doing so would recreate the
 * same rejection and loop forever with no user-visible retry surface — the
 * error screen exposes a manual "Try again" button that navigates to the
 * Ledger-SSO handoff (`buildLedgerSsoLoginPath()`), which starts a fresh
 * "Sign in with Ledger" PKCE handoff from a user-initiated click. Using
 * the plain `buildLoginPath()` credentials form here would send the same
 * Ledger-first admin (who has no `wagepro_companies` row) to a form that
 * would 403 them again for a different reason.
 */
type OAuthCallbackPhase =
  | { status: 'idle' }
  | { status: 'processing' }
  | { status: 'error'; message: string };

function isOAuthCallbackBoot(): boolean {
  return (
    isOAuthCallbackUrl(window.location.href) ||
    getOAuthCallbackError(window.location.href) !== null
  );
}

function OAuthCallbackBoot({
  onDone,
}: {
  onDone: () => void;
}): React.JSX.Element {
  const startedRef = useRef(false);
  const [phase, setPhase] = useState<OAuthCallbackPhase>({
    status: 'processing',
  });

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const origin = window.location.origin;
    void handleOAuthCallback({
      currentUrl: window.location.href,
      completeSignIn,
      pkceDeps: {
        apiBaseUrl: resolveApiUrl(),
        origin,
        resource: resolveOAuthResource(currentRuntimeTarget(), origin),
      },
      navigateHome: () => {
        // Strip the spent code and state; leave the user on the app root so
        // the AuthProvider that mounts next reads the freshly-minted token.
        window.history.replaceState(null, '', `${origin}/`);
      },
    }).then((result) => {
      switch (result.kind) {
        case 'authenticated':
          // Drop cached tRPC clients so `getBearerTrpcClient()` picks up the
          // freshly-minted access token (`getStoredTokens()` → new value).
          resetBearerTrpcClient();
          resetTrpcClient();
          setPhase({ status: 'idle' });
          onDone();
          break;
        case 'error':
        case 'failed':
          setPhase({ status: 'error', message: result.error });
          break;
        case 'not-callback':
          // Not reachable — the parent only mounts this component when
          // isOAuthCallbackBoot() was true. Kept exhaustive for safety.
          setPhase({ status: 'idle' });
          onDone();
          break;
      }
    });
  }, [onDone]);

  if (phase.status === 'error') {
    return (
      <GrantErrorScreen
        retry={() => {
          // User-initiated click after a rejected callback — start the
          // Ledger-SSO handoff again rather than the plain credentials form.
          // See this component's docblock and `AuthStatus`'s
          // `needs_ledger_sso` case for why the credentials form is wrong
          // for a caller who just held (and lost) a Ledger consent.
          window.location.href = buildLedgerSsoLoginPath();
        }}
      />
    );
  }
  // In-flight: reuse the same localised placeholder every other boot state
  // uses, so the user sees ONE continuous "Loading your WagePro account…" surface
  // from the consent screen all the way to the first painted app frame.
  // `forceConnectingCopy` is set because this component mounts BEFORE
  // `<AuthProvider>`, so no `AuthStatus` is available — but we KNOW the
  // caller is mid-Ledger-callback, so the copy is correct here.
  return <LocalisedBootPlaceholder forceConnectingCopy />;
}

// Bootstrap i18n then mount the real app.
void initI18n().then((initialState) => {
  function WageProApp(): React.JSX.Element {
    const [i18n, setI18n] = useState<I18nState>(initialState);
    // Pre-auth, pathname-gated public screens — checked BEFORE
    // <AuthProvider><AuthGate/></AuthProvider> mounts below, same mechanism
    // as `apps/tax-web/src/App.tsx`'s `ESS_ACCEPT_PATH` (`useState(() =>
    // window.location.pathname === PATH)`, early-return, `onDone` clears the
    // path via `history.replaceState`). None of these screens need a
    // session or OAuth token to render — that is the entire point of a
    // signup / login / invite-accept landing page.
    const [showSignup] = useState(
      () => window.location.pathname === SIGNUP_PATH,
    );
    const [showLogin] = useState(() => window.location.pathname === LOGIN_PATH);
    const [showInviteAccept] = useState(
      () => window.location.pathname === INVITE_ACCEPT_PATH,
    );
    // "First time signing in?" — the one-time invite code channel.
    const [showInviteCode] = useState(
      () => window.location.pathname === INVITE_CODE_PATH,
    );
    // Snapshot once at boot — same pattern as the pathname-gated screens
    // above. The exchange strips the code, so re-reading `location.href` on
    // a subsequent render would flip this to false and unmount the gate
    // mid-work; a state snapshot pins the initial value.
    const [isCallbackBoot, setIsCallbackBoot] = useState(() =>
      isOAuthCallbackBoot(),
    );

    // Latest-wins: each switch loads a bundle asynchronously, and the Profile
    // screen's language control switches on every arrow-key press — two quick
    // switches must not resolve out of order and land on the one passed over.
    const [runLocaleSwitch] = useState(create_latest_wins);
    const handleLocaleChange = (next: WageProLocale): void => {
      runLocaleSwitch(switchLocale(next), setI18n);
    };

    return (
      <I18nProvider
        bundle={i18n.bundle}
        locale={i18n.locale}
        fallback="en-001"
        fallbackBundle={i18n.fallbackBundle}
        onLocaleChange={(next) => handleLocaleChange(next as WageProLocale)}
      >
        {showSignup ? (
          <WageProSignupScreen />
        ) : showLogin ? (
          <WageProLoginScreen />
        ) : showInviteAccept ? (
          <InviteAcceptScreen />
        ) : showInviteCode ? (
          <InviteCodeScreen />
        ) : isCallbackBoot ? (
          <OAuthCallbackBoot
            onDone={() => {
              setIsCallbackBoot(false);
            }}
          />
        ) : (
          <AuthProvider>
            <AuthGate />
          </AuthProvider>
        )}
        {/*
         * Mount point for `toast()` calls (post-connect "Connected to
         * Ledger · <Org>" success toast in the first-run setup flow, and
         * any other in-app toasts — e.g. termination confirmation). No-op
         * when unused. Offset so the stack clears the employee bottom tab
         * bar on phones/tablets (see `toaster_offset.ts`).
         */}
        <Toaster
          position="bottom-right"
          offset={WAGEPRO_TOASTER_OFFSET}
          mobileOffset={WAGEPRO_TOASTER_MOBILE_OFFSET}
        />
      </I18nProvider>
    );
  }

  root.render(
    <React.StrictMode>
      <WageProApp />
    </React.StrictMode>,
  );
});
